// =============================================
// CallDocumentModal — תיעוד שיחה טלפונית
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { motion, AnimatePresence } from 'framer-motion';
import { PhoneCall, FileText, X } from 'lucide-react';
import type { CallCenterAlertDto } from '../../services/api';

const colors = {
  primary: '#0d9488',
  warning: '#f59e0b',
  warningBorder: '#fcd34d',
  muted: '#6b7280',
  mutedLight: '#f3f4f6',
  text: '#1f2937',
  border: '#e5e7eb',
  white: '#ffffff',
};

interface CallDocState {
  alertId: string;
  contactName: string;
  contactPhone: string;
  actionType: string;
  note: string;
}

interface CallDocumentModalProps {
  callDoc: CallDocState | null;
  alert: CallCenterAlertDto;
  isUpdating: boolean;
  onNoteChange: (note: string) => void;
  onSave: () => void;
  onClose: () => void;
}

export function CallDocumentModal({
  callDoc,
  alert,
  isUpdating,
  onNoteChange,
  onSave,
  onClose,
}: CallDocumentModalProps) {
  const isVisible = callDoc !== null && callDoc.alertId === alert.alertId;

  return (
    <AnimatePresence>
      {isVisible && callDoc && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          style={{
            padding: '14px',
            borderRadius: '10px',
            backgroundColor: '#fefce8',
            border: `1px solid ${colors.warningBorder}`,
            marginBottom: '12px',
            overflow: 'hidden',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px',
          }}>
            <p style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
              <PhoneCall
                size={14}
                style={{ display: 'inline', verticalAlign: 'middle', marginLeft: '4px' }}
              />
              תיעוד שיחה עם {callDoc.contactName}
            </p>
            <button
              onClick={onClose}
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px' }}
            >
              <X size={16} color={colors.muted} />
            </button>
          </div>

          <textarea
            value={callDoc.note}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder="תעד את תוכן השיחה (חובה)..."
            rows={3}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              fontSize: '13px',
              direction: 'rtl',
              resize: 'vertical',
              boxSizing: 'border-box',
              marginBottom: '8px',
            }}
            autoFocus
          />

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={onSave}
              disabled={isUpdating || !callDoc.note.trim()}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: callDoc.note.trim() ? colors.primary : colors.mutedLight,
                color: callDoc.note.trim() ? colors.white : colors.muted,
                cursor: callDoc.note.trim() ? 'pointer' : 'not-allowed',
                fontSize: '13px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <FileText size={14} /> שמור תיעוד
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.white,
                cursor: 'pointer',
                fontSize: '13px',
                color: colors.muted,
              }}
            >
              ביטול
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
